import React, { FC, useMemo } from "react";
import type { HomePageProps, PageProps } from "@/types";
import {
  HomeHero,
  HomeLatestInsights,
  LatestNews,
  HomePay,
  HomePostHero,
  HomeWhatYouCanDo,
  HomeWhyPayall,
  HomeWorld,
  HomeZeroIn,
  Ecosystem,
} from "../home";
import { HomeRealTimeTransactions } from "../home";
import { WhitePaperDownloadCTA } from "../home/white-paper-download-cta";

export const Home: FC<PageProps<HomePageProps>> = (props) => {
  // Merge Insights + News for the *homepage LatestInsights section only*
  const combinedLatest = useMemo(() => {
    const a = props.data.latestInsights ?? [];
    const b = props.data.latestNews ?? [];
    const merged = [...a, ...b];

    // De-dupe by group:slug (adjust to just slug if globally unique)
    const seen = new Set<string>();
    const deduped = merged.filter((p: any) => {
      const key = `${p?.group ?? ""}:${p?.slug ?? ""}`;
      if (!p?.slug || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Newest first; items without a date fall to the end
    deduped.sort((x: any, y: any) => {
      const dx = new Date(x?.date || 0).getTime();
      const dy = new Date(y?.date || 0).getTime();
      return dy - dx;
    });

    return deduped.slice(0, 4);
  }, [props.data.latestInsights, props.data.latestNews]);

  return (
    <div>
      <HomeHero
        title={props.data.title}
        description={props.data.description}
        getInTouch={props.meta.translation.home.hero.getInTouch}
        discover={props.meta.translation.home.hero.discover}
      />
      {/* <HomePostHero
        title={props.data.postHeroTitle}
        cols={props.data.postHeroCols}
      /> */}

      {/* Use the merged list here */}
      {combinedLatest.length > 0 && (
        <HomeLatestInsights
          title={props.meta.translation.home.latestInsights.title}
          viewAll={props.meta.translation.home.latestInsights.viewAll}
          readMore={props.meta.translation.home.latestInsights.readMore}
          posts={combinedLatest}
        />
      )}

      {props.data.downloadWhitePaperCta && (
        <WhitePaperDownloadCTA
          cta={props.data.downloadWhitePaperCta}
          contentModal={props.data.whitePaperDownloadModal}
          pageSource={props.data.title}
        />
      )}
      <Ecosystem
        subheader={props.data.ecosystemSubheader}
        header={props.data.ecosystemHeader}
        ecosystemCards={props.data.ecosystemCards}
        missionAndVisionCards={props.data.missionAndVisionCards}
        content={props.data.ecosystemParagraph}
      />
      <HomeWhatYouCanDo
        title={props.data.whatYouCanDoTitle}
        cards={props.data.whatYouCanDoCards}
        values={props.data.whatYouCanDoValues}
        cta={props.data.factsheetCta}
      />
      <HomePay title={props.data.payTitle} cards={props.data.payCards} />
      <HomeWhyPayall
        cards={props.data.whyPayallCards}
        whyPayAllText={props.meta.translation.home.whyPayall.label}
      />
      <HomeZeroIn
        title={props.data.zeroInTitle}
        content={props.data.zeroInContent}
        background={props.data.zeroInBackground}
      />
      <HomeRealTimeTransactions
        content={props.data.realTimeTransactionContent}
        image={props.data.realTimeTransactionImage}
      />
      <HomeWorld
        title={props.data.worldTitle}
        description={props.data.worldDescription}
        text={props.data.worldText}
        tags={props.data.worldTags}
      />

      {/* Keep the separate Latest News section as-is */}
      {props.data.latestNews?.length > 0 && (
        <LatestNews
          title={props.meta.translation.home.latestNews.title}
          viewAll={props.meta.translation.home.latestNews.viewAll}
          posts={props.data.latestNews}
        />
      )}
    </div>
  );
};
